/**
 * The approved Coca-Cola Academy palette, transcribed from the reference
 * project's DESIGN.md frontmatter. These are the ONLY brand colors.
 *
 * Values are authored in sRGB hex (as designers specified them). Three.js r152+
 * renders in linear space, so anything used as a material color must be built
 * via `srgb()` which tags the color space correctly. Using `new Color(0xDF1725)`
 * directly would wash out — that's the classic "why is my red pink" bug.
 */
import { Color, SRGBColorSpace } from 'three';

export const HEX = {
  cocaColaRed: 0xdf1725,
  // Sampled from the supplied 21Sep-logo-red-bg.png brand artwork.
  logoRed: 0xd1232a,
  actionRed: 0xf40009,
  hubGreen: 0x173721,
  deepGreen: 0x3c5142,
  softGreen: 0xa4cda8,
  recycleGreen: 0x74ac4b,
  goldenYellow: 0xf8c226,
  warmCream: 0xfce9cc,
  mintPanel: 0xddf2e0,
  white: 0xffffff,
  ink: 0x111111,
  bodyText: 0x333333,
  mutedText: 0x666666,
} as const;

export type PaletteKey = keyof typeof HEX;

/** Build a Three.Color from an sRGB hex, correctly converted to working space. */
export function srgb(hex: number): Color {
  const c = new Color();
  c.setHex(hex, SRGBColorSpace);
  return c;
}

/** Palette as ready-to-use Three colors. */
export const C: Record<PaletteKey, Color> = Object.fromEntries(
  Object.entries(HEX).map(([k, v]) => [k, srgb(v)])
) as Record<PaletteKey, Color>;

/** CSS custom properties, so DOM HUD and WebGL never drift apart. */
export const CSS_VARS: Record<string, string> = Object.fromEntries(
  Object.entries(HEX).map(([k, v]) => [
    `--cc-${k.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase())}`,
    '#' + v.toString(16).padStart(6, '0'),
  ])
);

/** Inject palette + shared tactile tokens into :root once at boot. */
export function installCssVars(doc: Document = document): void {
  const el = doc.documentElement;
  for (const [k, v] of Object.entries(CSS_VARS)) el.style.setProperty(k, v);
  // Tactile design tokens from DESIGN.md section 4 (Elevation).
  el.style.setProperty('--cc-shadow-button', '3px 4px 0 rgba(0,0,0,0.25)');
  el.style.setProperty('--cc-shadow-card', '6px 7px 0 rgba(0,0,0,0.25)');
  el.style.setProperty('--cc-scrim', 'rgba(0,0,0,0.35)');
  el.style.setProperty('--cc-radius-sm', '8px');
  el.style.setProperty('--cc-radius-md', '12px');
  el.style.setProperty('--cc-radius-pill', '999px');
  el.style.setProperty('--cc-ink-border', '3px');
}

/**
 * Hong Kong plastic recycling codes 1-6, governing Game 2.
 * Melting points and properties are real, and drive the scanner readouts.
 */
export interface PlasticCode {
  code: 1 | 2 | 3 | 4 | 5 | 6;
  abbr: string;
  name: string;
  /** Celsius; the scanner's primary readout. */
  meltingPointC: [number, number];
  /** g/cm^3 — drives the "sinks or floats in water" dissolution test. */
  densityGcm3: [number, number];
  floatsInWater: boolean;
  /** Short physical tells shown as chips on the match board. */
  tells: string[];
  tint: number;
  examples: string[];
}

export const PLASTICS: readonly PlasticCode[] = [
  {
    code: 1,
    abbr: 'PET',
    name: 'Polyethylene Terephthalate',
    meltingPointC: [250, 260],
    densityGcm3: [1.38, 1.39],
    floatsInWater: false,
    tells: ['Clear and glossy', 'Sinks in water', 'Crinkles loudly'],
    tint: 0x9fd8e8,
    examples: ['Soft drink bottle', 'Water bottle'],
  },
  {
    code: 2,
    abbr: 'HDPE',
    name: 'High-Density Polyethylene',
    meltingPointC: [120, 140],
    densityGcm3: [0.94, 0.97],
    floatsInWater: true,
    tells: ['Stiff and cloudy', 'Floats in water', 'Waxy to touch'],
    tint: 0xe8e4da,
    examples: ['Milk jug', 'Detergent bottle'],
  },
  {
    code: 3,
    abbr: 'PVC',
    name: 'Polyvinyl Chloride',
    meltingPointC: [100, 260],
    densityGcm3: [1.16, 1.45],
    floatsInWater: false,
    tells: ['Rigid, sharp edges', 'Sinks in water', 'Burns with green flame'],
    tint: 0xd8c8a8,
    examples: ['Pipe', 'Cling film'],
  },
  {
    code: 4,
    abbr: 'LDPE',
    name: 'Low-Density Polyethylene',
    meltingPointC: [105, 115],
    densityGcm3: [0.91, 0.93],
    floatsInWater: true,
    tells: ['Soft and floppy', 'Floats in water', 'Stretches before tearing'],
    tint: 0xf0f0e6,
    examples: ['Shopping bag', 'Squeeze bottle'],
  },
  {
    code: 5,
    abbr: 'PP',
    name: 'Polypropylene',
    meltingPointC: [160, 170],
    densityGcm3: [0.85, 0.92],
    floatsInWater: true,
    tells: ['Bends without cracking', 'Floats in water', 'Heat resistant'],
    tint: 0xf5d9c0,
    examples: ['Bottle cap', 'Yoghurt tub'],
  },
  {
    code: 6,
    abbr: 'PS',
    name: 'Polystyrene',
    meltingPointC: [210, 249],
    densityGcm3: [1.04, 1.07],
    floatsInWater: false,
    tells: ['Brittle, snaps cleanly', 'Sinks in water', 'Often foamed'],
    tint: 0xece8ee,
    examples: ['Foam takeaway box', 'Disposable cutlery'],
  },
] as const;
